import React, { useState } from "react";
import i18n from "utils/i18n";
import _ from "lodash";
import GraphqlBuilder from "utils/request/graphql/builder";
import Button from 'theme/Button'
import { StyleSheet, View } from 'react-native'
import { Column, Container, Grid, Row } from 'components/atoms/BootstrapGrid'
import CheckBox from 'theme/CheckBox'
import Text from "theme/Text";
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants, rightActivePrefixes, rightPrefixes } from "utils/constants/management-right-constants";
import { colorList } from "utils/colors";
import MutationManagementUserRights from "graphql/mutations/management/MutationManagementUserRights";
import HelperString from 'utils/helper-string'

const ManagementEmployeesEmployeeAdminRights = ( { employee } ) => {
	
	const [ renderKey, setRenderKey ] = useState( HelperString.random() )
	const [ isSubmittingAdmin, setIsSubmittingAdmin ] = useState( false )
	
	const reRender = () => {
		setRenderKey( HelperString.random() )
	}
	
	const renderAdminRightsTree = ( id, tree_items, level = false, disabled = false ) => {
		let rendered_rights = [];
		
		tree_items.forEach( ( tree_right, tree_right_key ) => {
			rendered_rights.push( renderAdminRightsItem( `${ id }_${ _.get( tree_right, 'id', tree_right_key ) }`, tree_right, level, disabled ) );
			
			const sub_right_items = _.get( tree_right, 'items', [] );
			
			if ( sub_right_items.length > 0 ) {
				const tree_disabled = ( disabled || !( _.get( tree_right, rightPrefixes.VIEW, false ) !== false && HelperManagementRights.hasViewRight( _.get( tree_right, rightPrefixes.VIEW ), employee ) ) );
				
				rendered_rights = [
					...rendered_rights,
					...renderAdminRightsTree( tree_right.id, sub_right_items, ( level !== false ? `${ level }_sub` : 'sub' ), tree_disabled )
				];
			}
		} );
		
		return rendered_rights;
	}
	
	const renderAdminRightsItem = ( key, right, sub_level, disabled = false ) => {
		const label_style = sub_level === false ? styles[ `rights_column_label` ] : styles[ `rights_column_label_${ sub_level }` ];
		const label_text_style = sub_level === false ? styles[ `rights_column_label_text` ] : styles[ `rights_column_label_text_${ sub_level }` ];
		
		return (
			<Row
				key={ key }
				style={ styles.rights_row }>
				
				<Column
					span={ 8 }
					style={ label_style }>
					<Text style={ label_text_style }>
						{ i18n.t( `management:user_rights.${ right.label }` ) }
					</Text>
				</Column>
				
				<Choose>
					<When condition={ _.get( right, 'view', null ) !== null }>
						<Column
							span={ 2 }
							style={ styles.rights_column_checkbox }>
							
							<Choose>
								<When condition={ disabled === false }>
									<CheckBox
										checked={ HelperManagementRights.hasViewRight( right.view, employee ) }
										onChange={ toggleViewRight.bind( this, right.view, _.get( right, 'edit', null ) ) }
									/>
								</When>
								<Otherwise>
									<CheckBox disabled={ true } />
								</Otherwise>
							</Choose>
						</Column>
					</When>
					<Otherwise>
						<Column
							span={ 2 }
							style={ styles.rights_column_checkbox }>
							
							<Text> </Text>
						</Column>
					</Otherwise>
				</Choose>
				
				<Choose>
					<When condition={ _.get( right, 'edit', null ) !== null }>
						<Column
							span={ 2 }
							style={ styles.rights_column_checkbox }>
							<Choose>
								<When condition={ ( _.get( right, 'view', null ) === null || HelperManagementRights.hasViewRight( right.view, employee ) ) && disabled === false }>
									<CheckBox
										checked={ HelperManagementRights.hasEditRight( right.edit, employee ) }
										onChange={ toggleEditRight.bind( this, right.edit, _.get( right, 'view', null ) ) }
									/>
								</When>
								<Otherwise>
									<CheckBox disabled={ true } />
								</Otherwise>
							</Choose>
						</Column>
					</When>
					<Otherwise>
						<Column
							span={ 2 }
							style={ styles.rights_column_checkbox }>
							
							<Text> </Text>
						</Column>
					</Otherwise>
				</Choose>
			</Row>
		);
	}
	
	const toggleViewRight = ( view_right, edit_right ) => {
		let new_state = HelperManagementRights.hasViewRight( view_right, employee ) ? rightActivePrefixes.INACTIVE : rightActivePrefixes.ACTIVE;
		
		HelperManagementRights.setViewRight( view_right, new_state, employee );
		
		if ( new_state === rightActivePrefixes.INACTIVE && edit_right !== null ) {
			HelperManagementRights.setEditRight( edit_right, rightActivePrefixes.INACTIVE, employee );
		}
		
		reRender();
	}
	
	const toggleEditRight = ( edit_right, view_right ) => {
		let new_state = HelperManagementRights.hasEditRight( edit_right, employee ) ? rightActivePrefixes.INACTIVE : rightActivePrefixes.ACTIVE;
		
		HelperManagementRights.setEditRight( edit_right, new_state, employee );
		
		reRender();
	}
	
	const mutateUserRights = () => {
		let flat_rights = HelperManagementRights.getAllRightsFlat( employee );
		
		const graphqlBuilder = new GraphqlBuilder();
		
		flat_rights.forEach( right => {
			graphqlBuilder.addQuery( `mutation_user_right_${ right.id }`, MutationManagementUserRights, {
				user_right: right.id,
				active: right.active,
				user_guid: _.get( employee, [
					'user',
					'guid'
				] )
			} );
		} );
		
		graphqlBuilder
			.setOnStart( () => {
				setIsSubmittingAdmin( true )
			})
			.setOnSuccess( result => {
				setIsSubmittingAdmin( false )
			})
			.setOnError( result => {
				setIsSubmittingAdmin( false )
			})
			.execute()
	}
	
	const managementRightsTree = {
		DASHBOARD: {
			id: 'dashboard',
			label: 'dashboard',
			items: [
				{
					id: 'dashboard',
					label: 'dashboard',
					view: managementRightsConstants.VIEW.DASHBOARD,
					items: [
						{
							label: 'presence',
							view: managementRightsConstants.VIEW.PRESENCE,
							edit: null
						}
					]
				}
			]
		},
		BOOKINGS: {
			id: 'bookings',
			label: 'bookings',
			items: [
				{
					id: 'bookings_workplaces',
					label: 'bookings_workplaces',
					view: managementRightsConstants.VIEW.BOOKINGS_WORKPLACES,
					items: [
						{
							id: 'bookings_workplaces_cancel',
							label: 'bookings_cancel',
							edit: managementRightsConstants.EDIT.BOOKINGS_WORKPLACES_CANCEL
						},
						{
							id: 'bookings_workplaces_scan_in',
							label: 'bookings_scan_in',
							edit: managementRightsConstants.EDIT.BOOKINGS_WORKPLACES_SCAN_IN
						},
						{
							id: 'bookings_workplaces_accept_status',
							label: 'bookings_accept_status',
							edit: managementRightsConstants.EDIT.BOOKINGS_WORKPLACES_ACCEPTED_STATUS
						}
					]
				},
				{
					id: 'bookings_meeting_rooms',
					label: 'bookings_meeting_rooms',
					view: managementRightsConstants.VIEW.BOOKINGS_MEETING_ROOMS,
					items: [
						{
							id: 'bookings_workplaces_cancel',
							label: 'bookings_cancel',
							edit: managementRightsConstants.EDIT.BOOKINGS_MEETING_ROOMS_CANCEL
						},
						{
							id: 'bookings_workplaces_scan_in',
							label: 'bookings_scan_in',
							edit: managementRightsConstants.EDIT.BOOKINGS_MEETING_ROOMS_SCAN_IN
						},
						{
							id: 'bookings_workplaces_accept_status',
							label: 'bookings_accept_status',
							edit: managementRightsConstants.EDIT.BOOKINGS_MEETING_ROOMS_ACCEPTED_STATUS
						}
					]
				},
				{
					id: 'bookings_lunches',
					label: 'bookings_lunches',
					view: managementRightsConstants.VIEW.BOOKINGS_LUNCH,
					items: [
						{
							id: 'bookings_workplaces_cancel',
							label: 'bookings_cancel',
							edit: managementRightsConstants.EDIT.BOOKINGS_LUNCH_CANCEL
						},
						{
							id: 'bookings_workplaces_scan_in',
							label: 'bookings_scan_in',
							edit: managementRightsConstants.EDIT.BOOKINGS_LUNCH_SCAN_IN
						},
						{
							id: 'bookings_workplaces_accept_status',
							label: 'bookings_accept_status',
							edit: managementRightsConstants.EDIT.BOOKINGS_LUNCH_ACCEPTED_STATUS
						}
					]
				},
				{
					id: 'bookings_parking_spots',
					label: 'bookings_parking_spots',
					view: managementRightsConstants.VIEW.BOOKINGS_PARKING_SPOTS,
					items: [
						{
							id: 'bookings_workplaces_cancel',
							label: 'bookings_cancel',
							edit: managementRightsConstants.EDIT.BOOKINGS_PARKING_SPOTS_CANCEL
						},
						{
							id: 'bookings_workplaces_scan_in',
							label: 'bookings_scan_in',
							edit: managementRightsConstants.EDIT.BOOKINGS_PARKING_SPOTS_SCAN_IN
						},
						{
							id: 'bookings_workplaces_accept_status',
							label: 'bookings_accept_status',
							edit: managementRightsConstants.EDIT.BOOKINGS_PARKING_SPOTS_ACCEPTED_STATUS
						}
					]
				}
			]
		},
		COMPANIES: {
			id: 'companies',
			label: 'companies',
			
			items: [
				{
					id: 'companies',
					label: 'companies',
					view: managementRightsConstants.VIEW.COMPANIES,
					edit: managementRightsConstants.EDIT.COMPANIES,
					items: [
						{
							id: 'departments',
							label: 'departments',
							view: managementRightsConstants.VIEW.DEPARTMENTS,
							edit: managementRightsConstants.EDIT.DEPARTMENTS,
						}
					]
				},
				{
					id: 'jobtitles',
					label: 'jobtitles',
					view: managementRightsConstants.VIEW.JOBTITLES,
					edit: managementRightsConstants.EDIT.JOBTITLES,
				},
				{
					id: 'teams',
					label: 'teams',
					view: managementRightsConstants.VIEW.TEAMS,
					edit: managementRightsConstants.EDIT.TEAMS,
					items: [
						{
							id: 'employees',
							label: 'employees',
							view: managementRightsConstants.VIEW.EMPLOYEES,
							edit: managementRightsConstants.EDIT.EMPLOYEES,
						}
					]
				}
			]
		},
		BUILDINGS: {
			id: 'buildings',
			label: 'buildings',
			items: [
				{
					id: 'buildings',
					label: 'buildings',
					view: managementRightsConstants.VIEW.BUILDINGS,
					edit: managementRightsConstants.EDIT.BUILDINGS,
					
					items: [
						{
							id: 'building_timeslots',
							label: 'timeslots',
							view: managementRightsConstants.VIEW.BUILDING_TIMESLOTS,
							edit: managementRightsConstants.EDIT.BUILDING_TIMESLOTS,
						},
						{
							id: 'building_contact_persons',
							label: 'contact_persons',
							view: managementRightsConstants.VIEW.BUILDING_CONTACT_PERSONS,
							edit: managementRightsConstants.EDIT.BUILDING_CONTACT_PERSONS,
						},
						{
							id: 'building_teams_employees',
							label: 'teams_employees',
							view: managementRightsConstants.VIEW.BUILDING_TEAMS_EMPLOYEES,
							edit: managementRightsConstants.EDIT.BUILDING_TEAMS_EMPLOYEES,
						},
						{
							id: 'floors',
							label: 'floors',
							view: managementRightsConstants.VIEW.FLOORS,
							edit: managementRightsConstants.EDIT.FLOORS,
							items: [
								{
									id: 'floor_timeslots',
									label: 'timeslots',
									view: managementRightsConstants.VIEW.FLOOR_TIMESLOTS,
									edit: managementRightsConstants.EDIT.FLOOR_TIMESLOTS,
								},
								{
									id: 'floor_teams_employees',
									label: 'teams_employees',
									view: managementRightsConstants.VIEW.FLOOR_TEAMS_EMPLOYEES,
									edit: managementRightsConstants.EDIT.FLOOR_TEAMS_EMPLOYEES,
								},
								{
									id: 'rooms',
									label: 'rooms',
									view: managementRightsConstants.VIEW.ROOMS,
									edit: managementRightsConstants.EDIT.ROOMS,
									items: [
										{
											id: 'room_timeslots',
											label: 'timeslots',
											view: managementRightsConstants.VIEW.ROOM_TIMESLOTS,
											edit: managementRightsConstants.EDIT.ROOM_TIMESLOTS,
										},
										{
											id: 'room_teams_employees',
											label: 'teams_employees',
											view: managementRightsConstants.VIEW.ROOM_TEAMS_EMPLOYEES,
											edit: managementRightsConstants.EDIT.ROOM_TEAMS_EMPLOYEES,
										}
									]
								}
							]
						},
					]
					
				}
			]
		},
		SETTINGS: {
			id: 'settings',
			label: 'settings',
			items: [
				{
					label: 'settings',
					view: managementRightsConstants.VIEW.SETTINGS,
					edit: managementRightsConstants.EDIT.SETTINGS
				}
			]
		},
	};
	
	let rendered_rights = [];
	_.forEach( managementRightsTree, tree => {
		rendered_rights.push(
			<View key={ tree.id } style={styles.viewFlexDirectionRow}>
				<View style={styles.viewFlex8}>
					<Text style={ styles.rights_header }>{ i18n.t( `management:user_rights.${ tree.label }` ) }</Text>
				</View>
				<View style={ styles.rights_column_checkbox }>
					<Text style={ styles.rights_header }>{ i18n.t( 'management:ui.read' ) }</Text>
				</View>
				<View style={ styles.rights_column_checkbox }>
					<Text style={ styles.rights_header }>{ i18n.t( 'management:ui.write' ) }</Text>
				</View>
			</View>
		);
		
		rendered_rights = [
			...rendered_rights,
			...renderAdminRightsTree( tree.id, _.get( tree, 'items', [] ) )
		];
	});
	
	return (
		<View>
			<Grid>
				<Container>
					{ rendered_rights }
				</Container>
			</Grid>
			
			<Button
				onPress={ mutateUserRights }
				isLoading={ isSubmittingAdmin }>
				
				{ i18n.t( 'management:pages.teams.employee.block_admin.button_save_rights' ) }
			</Button>
		</View>
	);
}

const styles = StyleSheet.create( {
	viewFlexDirectionRow: {
		flexDirection: 'row',
	},
	viewFlex8: {
		flex: 8,
	},
	rights_header: {
		fontWeight: "bold",
		paddingTop: 10,
		paddingBottom: 10
	},
	
	rights_row: {
		borderBottomWidth: 1,
		borderBottomColor: colorList.GREY.primary,
		paddingVertical: 5,
		marginBottom: 3
	},
	
	rights_column_label: {},
	rights_column_label_text: {},
	rights_column_label_sub: {},
	rights_column_label_text_sub: {
		marginLeft: 10
	},
	
	rights_column_label_sub_sub: {},
	rights_column_label_text_sub_sub: {
		marginLeft: 20
	},
	
	rights_column_label_sub_sub_sub: {},
	rights_column_label_text_sub_sub_sub: {
		marginLeft: 30
	},
	rights_column_label_sub_sub_sub_sub: {},
	rights_column_label_text_sub_sub_sub_sub: {
		marginLeft: 40
	},
	rights_column_checkbox: {
		flex: 2,
		alignItems: 'center'
	}
} );

export default ManagementEmployeesEmployeeAdminRights
